import { Box, Typography, IconButton, Stack, List, ListItem, ListItemText, ListItemIcon, Chip } from '@mui/material';
import { useNotifications } from '@/contexts/NotificationContext';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import { 
  NotificationEntity, 
  NotificationType, 
  NotificationPriority,
  NotificationCategory,
  notificationTypeToString,
  getPriorityColor 
} from '@/components/Classes/NotificationEntity';
import { format } from 'date-fns';
import Link from 'next/link';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { motion, AnimatePresence } from 'framer-motion';
// Import all required icons
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import WorkIcon from '@mui/icons-material/Work';
import ArticleIcon from '@mui/icons-material/Article';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CelebrationIcon from '@mui/icons-material/Celebration';
import DescriptionIcon from '@mui/icons-material/Description';
import UpdateIcon from '@mui/icons-material/Update';
import { useRouter } from 'next/navigation';

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case NotificationType.Info:
      return <InfoIcon sx={{ color: 'info.main' }} />;
    case NotificationType.Success:
      return <CheckCircleIcon sx={{ color: 'success.main' }} />;
    case NotificationType.Warning:
      return <WarningIcon sx={{ color: 'warning.main' }} />;
    case NotificationType.Error:
      return <ErrorIcon sx={{ color: 'error.main' }} />;
    case NotificationType.AdminAlert:
      return <NotificationsActiveIcon sx={{ color: 'error.main' }} />;
    case NotificationType.JobUpdate:
      return <WorkIcon sx={{ color: 'primary.main' }} />;
    case NotificationType.ApplicationUpdate:
      return <ArticleIcon sx={{ color: 'primary.main' }} />;
    case NotificationType.Deadline:
      return <AccessTimeIcon sx={{ color: 'warning.main' }} />;
    case NotificationType.Quote:
      return <FormatQuoteIcon sx={{ color: 'secondary.main' }} />;
    case NotificationType.Welcome:
      return <CelebrationIcon sx={{ color: 'success.main' }} />;
    case NotificationType.ResumeReminder:
      return <DescriptionIcon sx={{ color: 'info.main' }} />;
    case NotificationType.StatusUpdate:
      return <UpdateIcon sx={{ color: 'primary.main' }} />;
    default:
      return <InfoIcon sx={{ color: 'info.main' }} />;
  }
};

const getTimeString = (date: Date) => {
  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'Yesterday';
  return format(new Date(date), 'MMM dd');
};

const PopoverEvents = ({ user, onNavigate }: { user: any; onNavigate?: () => void }) => {
  const router = useRouter();
  const { notifications, markAsRead, deleteNotification, isLoading } = useNotifications();
  
  const unreadNotifications = notifications
    .filter(notification => !notification.isRead)
    .sort((a, b) => {
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const handleNotificationClick = (notification: NotificationEntity, e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons
    if (e.target instanceof HTMLElement && e.target.closest('button')) {
      return;
    }

    if (notification.actionLink) {
      router.push(notification.actionLink);
      if (!notification.isRead) {
        markAsRead(notification.id);
      }
      // Close the notification panel after navigation
      onNavigate?.();
    }
  };

  if (unreadNotifications.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <NotificationsIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography color="text.secondary">No new notifications</Typography>
      </Box>
    );
  }

  return (
    <List sx={{ p: 0, width: '100%' }}>
      <AnimatePresence mode="sync">
        {unreadNotifications.map((notification) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.2 }}
          >
            <ListItem
              onClick={(e) => handleNotificationClick(notification, e)}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                borderLeft: '4px solid',
                borderLeftColor: getPriorityColor(notification.priority),
                '&:hover': {
                  bgcolor: 'action.hover',
                  '& .notification-title': {
                    textDecoration: notification.actionLink ? 'underline' : 'none',
                  }
                },
                transition: 'all 0.2s',
                px: { xs: 1, sm: 2 },
                py: 1.5,
                position: 'relative',
                cursor: notification.actionLink ? 'pointer' : 'default',
              }}
              secondaryAction={
                <Stack direction="row" spacing={1}>
                  <IconButton 
                    size="small" 
                    onClick={() => markAsRead(notification.id)}
                    sx={{ 
                      color: 'success.main',
                      '&:hover': { bgcolor: 'success.lighter' } 
                    }}
                  >
                    <DoneIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => deleteNotification(notification.id)}
                    sx={{ 
                      color: 'error.main',
                      '&:hover': { bgcolor: 'error.lighter' } 
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              }
            >
              {notification.priority === NotificationPriority.Urgent && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 90,
                    bgcolor: 'error.main',
                    color: 'error.contrastText',
                    px: 1,
                    py: 0.25,
                    borderRadius: '4px',
                    fontSize: '0.675rem',
                    fontWeight: 'bold',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    boxShadow: 1,
                    zIndex: 1
                  }}
                >
                  Urgent
                </Box>
              )}
              <ListItemIcon sx={{ minWidth: 40 }}>
                {getNotificationIcon(notification.type)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography 
                    className="notification-title"
                    variant="body1" 
                    sx={{ 
                      fontWeight: 600,
                      color: notification.actionLink ? 'primary.main' : 'inherit',
                    }}
                  >
                    {notification.title}
                    {notification.actionLink && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          color: 'primary.main',
                          fontSize: '0.75rem',
                          verticalAlign: 'middle'
                        }}
                      >
                        &rarr;
                      </Box>
                    )}
                  </Typography>
                }
                secondary={
                  <Stack spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      {notification.message}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip 
                        size="small" 
                        label={NotificationCategory[notification.category]}
                        sx={{ height: 20 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {getTimeString(new Date(notification.createdAt))}
                      </Typography>
                    </Stack>
                    {notification.expiryDate && (
                      <Typography variant="caption" color="warning.main">
                        Expires: {format(new Date(notification.expiryDate), 'MMM dd, yyyy')}
                      </Typography>
                    )}
                  </Stack>
                }
                sx={{ pr: 6 }}
              />
            </ListItem>
          </motion.div>
        ))}
      </AnimatePresence>
    </List>
  );
};

export default PopoverEvents;
